import React, {
  useState,
  useMemo,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useRef,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'

import styled from 'styled-components'
import Joi from 'joi'
import toast from 'react-hot-toast'
import { RiPencilFill } from 'react-icons/ri'
import { FaTrashCan } from 'react-icons/fa6'

import { v4 as uuidv4 } from 'uuid'

import P from '../../../components/typography/P'
import { Button } from '../../../components/Button'
import { StandardInput, PhoneNumberInput } from '../../../components/Input'

import EmptyWrapper from '../../../components/EmptyWrapper'

import { getFromLocalStorage, parseJSON } from '../../../utils/util'

import { setCharacterReferences } from '../../../redux/feature/applicantSlice'

const CharacterReferences = forwardRef((props, ref) => {
  const dispatch = useDispatch()

  const { characterReferences } = useSelector((state) => state.applicant)

  const validationSchema = useMemo(() => {
    return Joi.object({
      crName: Joi.string().label('Full Name').required(),
      crEmail: Joi.string()
        .label('Email Address')
        .email({ tlds: { allow: false } })
        .required()
        .messages({
          'string.base': '"Email Address" must be a text value',
          'string.email': '"Email Address" must be a valid email address',
          'any.required': '"Email Address" is required',
        }),
      crPhone: Joi.string().label('Contact Number').required(),
    })
  }, [])

  const formRef = useRef(null)

  const [fieldErrors, setFieldErrors] = useState({})
  const [showForm, setShowForm] = useState(false)

  const [id, setId] = useState(uuidv4())

  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')
  const [contactNumber, setContactNumber] = useState('')

  const [isEdit, setIsEdit] = useState(false)

  const applicantCVData = useMemo(
    () => getFromLocalStorage('applicant-cv-data'),
    []
  )

  const resetFields = () => {
    setFullName('')
    setEmail('')
    setContactNumber('')
  }

  /**
   * @returns {boolean} Whether the form data is valid.
   */
  const handleNext = () => {
    const updatedApplicantCVData = {
      ...applicantCVData,
      character_reference: JSON.stringify(characterReferences),
    }

    // save to local storage...
    localStorage.setItem(
      'applicant-cv-data',
      JSON.stringify(updatedApplicantCVData)
    )

    return true
  }

  const handleShowForm = () => {
    setFieldErrors({})
    setIsEdit(false)
    setShowForm(true)
  }

  const handleCloseForm = () => {
    setShowForm(false)
    resetFields()
  }

  const handleSaveCharacterReference = () => {
    const payload = {
      crName: fullName,
      crEmail: email,
      crPhone: contactNumber,
    }

    // Validate form data against schema, collecting all errors
    const result = validationSchema.validate(payload, { abortEarly: false })

    // If there are validation errors, set error messages for fields and notify the user
    if (result.error) {
      const validationErrors = result.error.details.reduce((acc, curr) => {
        acc[curr.context.key] = curr.message.replace(/"/g, '')
        return acc
      }, {})

      setFieldErrors(validationErrors)
      toast.error('Please fix the validation errors to continue.')

      return
    }

    let newCharacterReferences = []

    if (isEdit) {
      payload.id = id
      newCharacterReferences = characterReferences.map((e) =>
        String(e.id) === String(id) ? payload : e
      )
    } else {
      payload.id = uuidv4()
      newCharacterReferences = [...characterReferences, payload]
    }

    dispatch(setCharacterReferences(newCharacterReferences))

    setShowForm(false)
    setIsEdit(false)
    resetFields()
  }

  const handleEditCharacterReference = (data) => {
    setIsEdit(true)

    setId(data?.id)
    setFullName(data?.crName)
    setEmail(data?.crEmail)
    setContactNumber(data?.crPhone)

    setShowForm(true)
    setFieldErrors({})
  }

  const handleDeleteCharacterReference = (data) => {
    const removedCharacterReference = characterReferences.filter(
      (e) => e.id !== data.id
    )

    dispatch(setCharacterReferences(removedCharacterReference))

    setShowForm(false)
  }

  useEffect(() => {
    if (applicantCVData) {
      const savedCharacterReferences =
        parseJSON(applicantCVData?.character_reference, []) || []

      dispatch(setCharacterReferences(savedCharacterReferences))
    }
  }, [])

  useEffect(() => {
    // automatically scroll to the form
    if (showForm && formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [showForm])

  useImperativeHandle(ref, () => ({
    onNext: handleNext,
  }))

  return (
    <StyledCharacterReferences>
      <P size="1.5rem" weight="500" mb="0.25rem">
        Character References
      </P>

      <P color="#6F6F6F" mb="1rem">
        List people who can vouch for your character, work ethic, and personal
        qualities.
      </P>
      <Button handleClick={handleShowForm} width="50%" btnClass="primary">
        Add new character reference
      </Button>
      <Container>
        {characterReferences?.length > 0 ? (
          characterReferences.map((e, i) => (
            <ListItem key={i}>
              <ListItemHeader>
                <P weight="500">{e.crName}</P>

                <ListItemIconsWrapper>
                  <RiPencilFill
                    className="icon"
                    onClick={() => handleEditCharacterReference(e)}
                  />
                  <FaTrashCan
                    className="icon"
                    onClick={() => handleDeleteCharacterReference(e)}
                  />
                </ListItemIconsWrapper>
              </ListItemHeader>
              <ListItemBody>
                <P>{e.crEmail}</P>
                <P>{e.crPhone}</P>
              </ListItemBody>
            </ListItem>
          ))
        ) : (
          <EmptyWrapper maxHeight="100%" marginBottom="1rem" marginTop="1rem">
            You have no character referencess.
          </EmptyWrapper>
        )}

        {showForm && (
          <FormContainer ref={formRef}>
            <FormGroup>
              <StandardInput
                title="Full Name"
                type="text"
                value={fullName}
                onChange={(value) => setFullName(value)}
                required
                wrapperClass={fieldErrors.crName && 'error'}
                subText={fieldErrors.crName}
                placeholder="Enter Full Name"
                marginBottom="0.5rem"
              />
            </FormGroup>
            <FormGroup>
              <StandardInput
                title="Email Address"
                type="email"
                value={email}
                onChange={(value) => setEmail(value)}
                required
                wrapperClass={fieldErrors.crEmail && 'error'}
                subText={fieldErrors.crEmail}
                placeholder="Enter Email Address"
                marginBottom="0.5rem"
              />
            </FormGroup>

            <FormGroup className="phone__number">
              <PhoneNumberInput
                title="Phone Number"
                country="ph"
                value={contactNumber}
                required
                onChange={(value) => setContactNumber(value)}
                wrapperClass={fieldErrors.crPhone && 'error'}
                subText={fieldErrors.crPhone}
              />
            </FormGroup>

            <ButtonWrapper>
              <Button
                handleClick={handleSaveCharacterReference}
                btnClass="success"
              >
                Save Record
              </Button>
              <Button handleClick={handleCloseForm} btnClass="primary">
                Close Form
              </Button>
            </ButtonWrapper>
          </FormContainer>
        )}
      </Container>
    </StyledCharacterReferences>
  )
})

const StyledCharacterReferences = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-top: 1rem;
`

const FormGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  width: 100%;

  &.phone__number {
    margin-bottom: 1.75rem;
  }
`

const Container = styled.div`
  display: flex;
  flex-direction: column;
`

const ListItem = styled.div`
  width: 100%;
  background: rgb(248, 249, 255);
  padding: 0.5rem;
  border-radius: 3px;
  position: relative;
  margin-top: 1rem;
  font-weight: 300;
`

const ListItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
`

const ListItemIconsWrapper = styled.div`
  .icon {
    font-size: 0.875rem;
    color: rgb(174, 179, 201);
    margin-left: 1rem;
    cursor: pointer;
  }

  .icon:hover {
    color: rgba(47, 52, 81, 1);
  }
`

const ListItemBody = styled.div`
  display: flex;
  flex-direction: column;
`

const FormContainer = styled.div`
  padding: 0.5rem;
  margin-top: 1rem;
`

const ButtonWrapper = styled.div`
  display: flex;
  gap: 1rem;
`

CharacterReferences.displayName = 'CharacterReferences'

export default CharacterReferences
